/** The pocket's thing level (spec §7.2; Part 10 §4): the thing's card, painted, as its header; You's profile; the suggestions
 * touching it ("To review" first); its facts grouped by relation; one row per bundle; then Forget, except for You and a
 * project, which anchors its canvas. It reads the focus view of the thing. */
import { useState } from "react";
import type { FocusView, MemoryFact, ThingKind } from "@/api/memory-focus";
import {
  Button,
  PocketRow,
  PocketSection,
  PocketStatement,
  Spinner,
} from "@/components/ui";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import { profileEntries } from "@/lib/knowledge-kinds";
import { reviewReasonLabel } from "@/lib/memory-review-reasons";
import {
  relationGroupKey,
  relationGroupLabel,
  thingDisplayName,
} from "@/lib/memory-thing-kinds";
import { useMemoryStore } from "@/stores/memory-store";
import { ThingCardFace } from "../shared/paint-card-faces";
import { ForgetThingDialog } from "./forget-thing-dialog";
import { bundleTitle, factSecondLine } from "./memory-pocket-helpers";
import { ThingAvatar } from "./thing-avatar";
/** You and a project stay: You holds the profile, a project anchors its canvas. */
const KEPT_KINDS: ReadonlySet<ThingKind> = new Set(["user_profile", "project"]);
function factGroups(
  view: FocusView,
  viewpointId: string,
): Array<{ key: string; label: string; facts: MemoryFact[] }> {
  const groups = new Map<
    string,
    { key: string; label: string; facts: MemoryFact[] }
  >();
  const facts = [
    ...new Map(
      view.ring.flatMap((slot) => slot.facts).map((fact) => [fact.id, fact]),
    ).values(),
  ];
  for (const fact of facts) {
    const key = relationGroupKey(fact, viewpointId);
    const group = groups.get(key) ?? {
      key,
      label: relationGroupLabel(fact, viewpointId),
      facts: [],
    };
    groups.set(key, { ...group, facts: [...group.facts, fact] });
  }
  return [...groups.values()].sort(
    (a, b) => b.facts.length - a.facts.length || a.label.localeCompare(b.label),
  );
}
export function PocketThingLevel({
  id,
  youName,
}: {
  id: string;
  youName: string;
}) {
  const t = useT(framesMemoryMessages);
  const common = useT(commonMessages);
  const [confirmForget, setConfirmForget] = useState(false);
  const view = useMemoryStore((s) =>
    s.focusViewId === id ? s.focusView : null,
  );
  const failed = useMemoryStore(
    (s) => s.focusError !== null && s.focusViewId !== id,
  );
  const store = () => useMemoryStore.getState();
  if (!view && failed)
    return (
      <PocketStatement
        size="empty"
        title={t("loadFailed")}
        testId="memory-thing-error"
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void store().loadFocus()}
            data-testid="memory-thing-retry"
          >
            {common("retry")}
          </Button>
        }
      />
    );
  if (!view)
    return (
      <div className="flex justify-center py-6">
        <Spinner className="h-4 w-4 text-muted" />
      </div>
    );
  const thing = view.focus;
  if (!thing?.id)
    return (
      <PocketStatement
        size="empty"
        title={t("remembersNothing")}
        testId="memory-thing-empty"
      />
    );
  const [viewpoint, groups, profile] = [
    thing.id,
    factGroups(view, thing.id),
    thing.profile ? profileEntries({ ...thing.profile }) : [],
  ];
  const document = thing.kind === "document" ? thing.description.trim() : "";
  return (
    <div data-testid="memory-thing-level">
      <ThingCardFace
        name={thingDisplayName(thing)}
        kind={thing.kind}
        description={
          thing.kind === "user_profile"
            ? youName
            : document
              ? undefined
              : thing.description.trim()
        }
        clamp={false}
        className="mb-1.5 rounded-preview [font-size:14px]"
        testId="memory-thing-statement"
      />
      {document ? (
        <p
          data-testid="memory-document-content"
          className="whitespace-pre-wrap break-words px-2 py-1 text-appkit-body text-primary"
        >
          {document}
        </p>
      ) : null}
      {profile.length > 0 ? (
        <PocketSection title={t("profile")}>
          {profile.map(([field, label, value]) => (
            <PocketRow
              key={field}
              testId={`memory-profile-${field}`}
              title={label}
              trailing={value}
            />
          ))}
        </PocketSection>
      ) : null}
      {view.suggestions.length > 0 ? (
        <PocketSection title={t("toReview")}>
          {view.suggestions.map((item) => (
            <PocketRow
              key={item.id}
              testId={`memory-thing-review-${item.id}`}
              title={item.claim}
              secondLine={reviewReasonLabel(item.reason)}
              status={{ tone: "question" }}
              chevron
              onSelect={() => store().openReview(item.id)}
            />
          ))}
        </PocketSection>
      ) : null}
      {groups.map((group) => (
        <PocketSection key={group.key} title={group.label}>
          {group.facts.map((fact) => (
            <PocketRow
              key={fact.id}
              testId={`memory-fact-row-${fact.id}`}
              title={fact.claim}
              secondLine={factSecondLine(fact, viewpoint)}
              chevron
              onSelect={() => store().select({ type: "fact", id: fact.id })}
            />
          ))}
        </PocketSection>
      ))}
      {view.bundles.length > 0 ? (
        <PocketSection title={t("alsoLinked")}>
          {view.bundles.map((bundle) => (
            <PocketRow
              key={bundle.kind}
              testId={`memory-bundle-row-${bundle.kind}`}
              leading={<ThingAvatar kind={bundle.kind} name="" />}
              title={bundleTitle(view, bundle.kind)}
              chevron
              onSelect={() =>
                store().openLevel({ level: "bundle", id: bundle.kind })
              }
            />
          ))}
        </PocketSection>
      ) : null}
      {!document &&
      groups.length + view.suggestions.length + view.bundles.length === 0 ? (
        <p className="px-2 py-1 text-appkit-caption text-muted">
          {t("learnedNothingAbout", { name: thingDisplayName(thing) })}
        </p>
      ) : null}
      {KEPT_KINDS.has(thing.kind) ? null : (
        <div className="mt-3 flex px-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirmForget(true)}
            data-testid="memory-thing-forget"
          >
            {t("forget")}
          </Button>
        </div>
      )}
      {confirmForget ? (
        <ForgetThingDialog
          thingId={thing.id}
          name={thingDisplayName(thing)}
          onClose={() => setConfirmForget(false)}
          testId="memory-thing-forget-confirm"
        />
      ) : null}
    </div>
  );
}
